import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './App.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import { GroundTruthService } from './services/groundTruthService';
import { IMasterGroundTruth } from './types/groundTruth';

/**
 * Ground Truth Detail View Component
 * Displays ground truth records in a layout similar to BondDetailView
 */
const GroundTruthDetailView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // State management
  const [record, setRecord] = useState<IMasterGroundTruth | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Load ground truth record
   */
  const loadRecord = useCallback(async () => {
    if (!id) {
      setError('No record ID provided');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await GroundTruthService.getById(id);
      setRecord(data);
    } catch (err: any) {
      console.error('Error loading ground truth record:', err);
      setError(err.message || 'Failed to load ground truth record');
    } finally {
      setLoading(false);
    }
  }, [id]);

  /**
   * Load record on mount and when ID changes
   */
  useEffect(() => {
    loadRecord();
  }, [loadRecord]);

  const formatDate = (dateValue: string | Date | undefined) => {
    if (!dateValue) {
      return 'N/A';
    }

    if (dateValue instanceof Date) {
      return dateValue.toLocaleDateString();
    }

    if (typeof dateValue === 'string' && dateValue.trim() === '') {
      return 'N/A';
    }

    try {
      const date = new Date(dateValue as string);
      if (!isNaN(date.getTime())) {
        return new Intl.DateTimeFormat(navigator.language, {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        }).format(date);
      }
    } catch {
      console.warn(`Unable to parse date: "${dateValue}"`);
    }

    return String(dateValue);
  };

  const handleBack = () => {
    navigate('/ground-truth');
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="bonds-loader-flex">
          <i className="fas fa-spinner fa-spin loader-icon" aria-label="Loading" />
          <span className="loader-text">{t('loading.groundTruthDetails', 'Loading ground truth details...')}</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <div className="error-message">
          <i className="fas fa-exclamation-triangle" style={{ marginRight: '10px', color: '#e74c3c' }}></i>
          {error}
        </div>
        <button onClick={handleBack} className="btn-primary" style={{ marginTop: '20px' }}>
          <i className="fas fa-arrow-left" style={{ marginRight: '8px' }}></i>
          {t('buttons.backToGroundTruth', 'Back to Ground Truth')}
        </button>
      </div>
    );
  }

  if (!record) {
    return (
      <div className="error-container">
        <div className="error-message">{t('groundTruth.notFound', 'Ground truth record not found')}</div>
        <button onClick={handleBack} className="btn-primary" style={{ marginTop: '20px' }}>
          <i className="fas fa-arrow-left" style={{ marginRight: '8px' }}></i>
          {t('buttons.backToGroundTruth', 'Back to Ground Truth')}
        </button>
      </div>
    );
  }

  // Check if this is a bonds-ground-truth record (has _original field)
  const isBondsRecord = (record as any)._original;
  const originalRecord = isBondsRecord ? (record as any)._original : null;

  return (
    <div className="bond-detail-page" style={{ width: '100%', maxWidth: '100%', padding: '0 20px', backgroundColor: '#f5f6f8', boxSizing: 'border-box' }}>
      <div className="bond-detail-header">
        <div className="bond-detail-nav">
          <button onClick={handleBack} className="back-button">
            <i className="fas fa-arrow-left"></i>
            {t('buttons.back')}
          </button>
        </div>
        <h2 className="bond-detail-title">{t('groundTruth.detailTitle', 'Ground Truth Detail')}</h2>
      </div>

      <div style={{ width: '100%', paddingLeft: '20px', paddingRight: '20px', boxSizing: 'border-box' }}>
        <div style={{ padding: '0' }}>
          {/* Overview Section */}
          <div className="detail-section combined-overview-summary" style={{ backgroundColor: '#ffffff' }}>
            <div className="combined-grid" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '24px' }}>
              {/* Overview (left) */}
              <div className="overview-section">
                <h3 style={{ color: '#333', textAlign: 'left', marginBottom: '12px', borderBottom: 'none', paddingBottom: 0 }}>Record Overview</h3>
                <div className="overview-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 24px' }}>
                  <div className="detail-item">
                    <div className="detail-label-small">Document ID</div>
                    <div className="detail-value-large" style={{ fontWeight: 'bold' }}>{record.document_id || 'N/A'}</div>
                  </div>
                  <div className="detail-item">
                    <div className="detail-label-small">Source Name</div>
                    <div className="detail-value-large">{record.source_name || 'N/A'}</div>
                  </div>
                  <div className="detail-item">
                    <div className="detail-label-small">Document Type</div>
                    <div className="detail-value-large">{record.document_type || 'N/A'}</div>
                  </div>
                  <div className="detail-item">
                    <div className="detail-label-small">Validation Status</div>
                    <div className="detail-value-large" style={{
                      color: record.validation_status === 'Validated' ? '#10b981' :
                             record.validation_status === 'Pending' ? '#f59e0b' :
                             record.validation_status === 'Failed' ? '#ef4444' : '#64748b',
                      fontWeight: '600'
                    }}>
                      {record.validation_status || 'N/A'}
                    </div>
                  </div>
                  {record.title && (
                    <div className="detail-item" style={{ gridColumn: '1 / -1' }}>
                      <div className="detail-label-small">Title</div>
                      <div className="detail-value-large">{record.title}</div>
                    </div>
                  )}
                </div>
              </div>

              {/* Summary (right) */}
              <div className="financial-summary" style={{ paddingLeft: '24px' }}>
                <h3 style={{ color: '#333', textAlign: 'left', marginBottom: '20px', borderBottom: 'none', paddingBottom: 0 }}>Summary</h3>
                {record.author && (
                  <div className="detail-item">
                    <div className="detail-label-small">Author</div>
                    <div className="detail-value-large">{record.author}</div>
                  </div>
                )}
                {record.publication_date && (
                  <div className="detail-item">
                    <div className="detail-label-small">Publication Date</div>
                    <div className="detail-value-large">{formatDate(record.publication_date)}</div>
                  </div>
                )}
                {record.confidence_scores?.overall !== undefined && (
                  <div className="detail-item">
                    <div className="detail-label-small">Confidence Score</div>
                    <div className="detail-value-large">
                      {(record.confidence_scores.overall * 100).toFixed(1)}%
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Keywords */}
          {record.keywords && record.keywords.length > 0 && (
            <div className="detail-section full-width" style={{ backgroundColor: '#ffffff', marginTop: '24px' }}>
              <h3 style={{ color: '#333', textAlign: 'left', marginBottom: '16px' }}>Keywords</h3>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {record.keywords.map((keyword, index) => (
                  <span key={index} style={{
                    padding: '4px 12px',
                    backgroundColor: '#e3f2fd',
                    color: '#1565c0',
                    borderRadius: '4px',
                    fontSize: '13px',
                    fontWeight: '500'
                  }}>
                    {keyword}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Summary Text */}
          {record.summary && (
            <div className="detail-section full-width" style={{ backgroundColor: '#ffffff', marginTop: '24px' }}>
              <h3 style={{ color: '#333', textAlign: 'left', marginBottom: '16px' }}>Summary</h3>
              <div className="detail-text" style={{ marginBottom: '16px', lineHeight: '1.6' }}>{record.summary}</div>
            </div>
          )}

          {/* Source URL */}
          {record.source_url && (
            <div className="detail-section full-width" style={{ backgroundColor: '#ffffff', marginTop: '24px' }}>
              <h3 style={{ color: '#333', textAlign: 'left', marginBottom: '16px' }}>Source</h3>
              <div className="detail-text">
                <a href={record.source_url} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', textDecoration: 'underline' }}>
                  {record.source_url}
                  <i className="fas fa-external-link-alt" style={{ marginLeft: '8px', fontSize: '12px' }}></i>
                </a>
              </div>
            </div>
          )}

          {/* Original Bond Data (if from bonds-ground-truth collection) */}
          {originalRecord && (
            <div className="detail-section full-width" style={{ backgroundColor: '#ffffff', marginTop: '24px' }}>
              <h3 style={{ color: '#333', textAlign: 'left', marginBottom: '16px' }}>Original Bond Data</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                {originalRecord.isin && (
                  <div className="detail-item">
                    <div className="detail-label-small">ISIN</div>
                    <div className="detail-value-large">{originalRecord.isin}</div>
                  </div>
                )}
                {originalRecord.issuer && (
                  <div className="detail-item">
                    <div className="detail-label-small">Issuer</div>
                    <div className="detail-value-large">{originalRecord.issuer}</div>
                  </div>
                )}
                {originalRecord.instrument_name && (
                  <div className="detail-item">
                    <div className="detail-label-small">Instrument Name</div>
                    <div className="detail-value-large">{originalRecord.instrument_name}</div>
                  </div>
                )}
                {originalRecord.issue_date && (
                  <div className="detail-item">
                    <div className="detail-label-small">Issue Date</div>
                    <div className="detail-value-large">{formatDate(originalRecord.issue_date)}</div>
                  </div>
                )}
                {originalRecord.maturity_date && (
                  <div className="detail-item">
                    <div className="detail-label-small">Maturity Date</div>
                    <div className="detail-value-large">{formatDate(originalRecord.maturity_date)}</div>
                  </div>
                )}
                {originalRecord.volume && (
                  <div className="detail-item">
                    <div className="detail-label-small">Volume</div>
                    <div className="detail-value-large">{originalRecord.volume}</div>
                  </div>
                )}
                {originalRecord.currency && (
                  <div className="detail-item">
                    <div className="detail-label-small">Currency</div>
                    <div className="detail-value-large">{originalRecord.currency}</div>
                  </div>
                )}
                {originalRecord.source && (
                  <div className="detail-item">
                    <div className="detail-label-small">Source</div>
                    <div className="detail-value-large">{originalRecord.source}</div>
                  </div>
                )}
              </div>
              {originalRecord.tags && originalRecord.tags.length > 0 && (
                <div style={{ marginTop: '16px' }}>
                  <div className="detail-label-small" style={{ marginBottom: '8px' }}>Tags</div>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {originalRecord.tags.map((tag: string, index: number) => (
                      <span key={index} style={{
                        padding: '4px 12px',
                        backgroundColor: tag === 'DELETE' ? '#fee2e2' : '#f3f4f6',
                        color: tag === 'DELETE' ? '#991b1b' : '#374151',
                        borderRadius: '4px',
                        fontSize: '13px',
                        fontWeight: '500'
                      }}>
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Audit Information */}
          <div className="detail-section full-width" style={{ backgroundColor: '#ffffff', marginTop: '24px' }}>
            <h3 style={{ color: '#333', textAlign: 'left', marginBottom: '16px' }}>Audit Information</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div className="detail-item">
                <div className="detail-label-small">Created At</div>
                <div className="detail-value-large">{formatDate(record.created_at)}</div>
              </div>
              <div className="detail-item">
                <div className="detail-label-small">Updated At</div>
                <div className="detail-value-large">{formatDate(record.updated_at)}</div>
              </div>
              <div className="detail-item">
                <div className="detail-label-small">Created By</div>
                <div className="detail-value-large">{record.created_by || 'System'}</div>
              </div>
              <div className="detail-item">
                <div className="detail-label-small">Current Version</div>
                <div className="detail-value-large">{record.current_version || 1}</div>
              </div>
            </div>
          </div>

          {/* Footer - Last Updated Information */}
          <div className="detail-section full-width" style={{ backgroundColor: '#ffffff', marginBottom: '20px', marginTop: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
              <div className="update-info">
                <i className="fas fa-clock"></i>
                {t('groundTruth.lastUpdated', 'Last Updated')}: {formatDate(record.updated_at)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GroundTruthDetailView;
